import type { DashboardNode, DashboardSchema, RunVerificationResult } from "../lib/types";
import { renderInlineMarkdown } from "../lib/markdown";

export interface DashboardRenderProps {
  schema: DashboardSchema;
  outputs: Record<string, unknown>;
  verificationResults?: RunVerificationResult[];
  allowedHosts?: string[];
}

function resolveBinding(outputs: Record<string, unknown>, binding?: string): unknown {
  if (!binding) return undefined;
  return binding.split(".").reduce<unknown>((current, key) => {
    if (current && typeof current === "object" && key in current) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, outputs);
}

function asRows(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === "object");
}

export function DashboardRenderer({
  schema,
  outputs,
  verificationResults = [],
  allowedHosts = [],
}: DashboardRenderProps) {
  return (
    <div className="tj-dashboard space-y-4" data-testid="dashboard">
      {schema.nodes.map((node) => (
        <DashboardNodeView
          key={node.id}
          node={node}
          outputs={outputs}
          verificationResults={verificationResults}
          allowedHosts={allowedHosts}
        />
      ))}
    </div>
  );
}

function DashboardNodeView({
  node,
  outputs,
  verificationResults,
  allowedHosts,
}: {
  node: DashboardNode;
  outputs: Record<string, unknown>;
  verificationResults: RunVerificationResult[];
  allowedHosts: string[];
}) {
  const value = resolveBinding(outputs, node.binding);
  switch (node.kind) {
    case "section":
      return (
        <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-4">
          <h3 className="serif-heading mb-3 text-lg">{node.title}</h3>
          <div className="space-y-3">
            {(node.children ?? []).map((child) => (
              <DashboardNodeView
                key={child.id}
                node={child}
                outputs={outputs}
                verificationResults={verificationResults}
                allowedHosts={allowedHosts}
              />
            ))}
          </div>
        </section>
      );
    case "metric":
      return (
        <div className="rounded-lg border border-[var(--tj-border)] p-3">
          <p className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">{node.label}</p>
          <p className="text-2xl font-semibold">{formatValue(value, node.format)}</p>
        </div>
      );
    case "progress": {
      const max = node.max && node.max > 0 ? node.max : 100;
      const current = typeof value === "number" ? value : 0;
      return (
        <div>
          <div className="flex justify-between text-xs text-[var(--tj-muted)]">
            <span>{node.label}</span>
            <span>{Math.round((current / max) * 100)}%</span>
          </div>
          <div className="mt-1 h-2 rounded bg-[var(--tj-border)]" role="progressbar" aria-valuenow={current} aria-valuemax={max}>
            <div className="h-2 rounded bg-[var(--tj-accent)]" style={{ width: `${Math.min(100, (current / max) * 100)}%` }} />
          </div>
        </div>
      );
    }
    case "table": {
      const rows = asRows(value);
      const columns = node.columns ?? [];
      return (
        <div>
          <h4 className="mb-2 font-medium">{node.title}</h4>
          {rows.length > 200 ? (
            <p className="text-xs text-[var(--tj-muted)]">Showing 200 of {rows.length} rows</p>
          ) : null}
          <div className="hidden overflow-x-auto sm:block">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} className="border-b px-2 py-1 text-left">
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 200).map((row, index) => (
                  <tr key={index}>
                    {columns.map((column) => (
                      <td key={column.key} className="border-b px-2 py-1">
                        {String(row[column.key] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-2 sm:hidden">
            {rows.slice(0, 200).map((row, index) => (
              <div key={index} className="rounded border border-[var(--tj-border)] p-2 text-sm">
                {columns.map((column) => (
                  <p key={column.key}>
                    <span className="text-[var(--tj-muted)]">{column.label}: </span>
                    {String(row[column.key] ?? "")}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "download_list": {
      const items = asRows(value);
      return (
        <div>
          <h4 className="mb-2 font-medium">{node.title}</h4>
          <ul className="space-y-1 text-sm">
            {items.map((item, index) => (
              <li key={index} className="rounded border border-[var(--tj-border)] px-2 py-1">
                {String(item.name ?? item.filename ?? "artifact")}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    case "checklist":
      return (
        <div>
          <h4 className="mb-2 font-medium">{node.title}</h4>
          <ul className="space-y-1 text-sm">
            {(node.test_ids ?? []).map((testId) => {
              const result = verificationResults.find((item) => item.test_id === testId);
              // Status comes from execution records, never from model-authored text.
              const outcome = result?.outcome;
              const passed = outcome === "pass";
              const label =
                outcome === "pass"
                  ? "passed"
                  : outcome === "not_applicable"
                    ? "not applicable"
                    : outcome === "error"
                      ? "error"
                      : outcome === "fail"
                        ? "failed"
                        : "not run";
              const color =
                outcome === "fail" || outcome === "error"
                  ? "text-red-700"
                  : outcome === "pass"
                    ? "text-green-700"
                    : "text-gray-500";
              return (
                <li key={testId} className={color}>
                  <span aria-hidden>{passed ? "✓" : outcome === "not_applicable" ? "–" : "✗"}</span>{" "}
                  {testId}: {label}
                </li>
              );
            })}
          </ul>
        </div>
      );
    case "timeline": {
      const items = asRows(value);
      return (
        <div>
          <h4 className="mb-2 font-medium">{node.title}</h4>
          <ol className="space-y-1 text-sm">
            {items.map((item, index) => (
              <li key={index}>
                <span className="text-[var(--tj-muted)]">{String(item.at ?? "")}</span> {String(item.label ?? "")}
              </li>
            ))}
          </ol>
        </div>
      );
    }
    case "key_value": {
      const items = asRows(value);
      return (
        <dl className="grid grid-cols-2 gap-2 text-sm">
          {items.map((item, index) => (
            <div key={index} className="contents">
              <dt className="text-[var(--tj-muted)]">{String(item.key ?? "")}</dt>
              <dd>{String(item.value ?? "")}</dd>
            </div>
          ))}
        </dl>
      );
    }
    case "text":
      return <p className="text-sm leading-relaxed">{renderInlineMarkdown(node.body ?? "", allowedHosts)}</p>;
    default:
      return (
        <div role="note" className="rounded border border-dashed border-[var(--tj-border)] p-3 text-sm text-[var(--tj-muted)]">
          Unsupported component: {String((node as { kind?: string }).kind ?? "unknown")}
        </div>
      );
  }
}

function formatValue(value: unknown, format?: string): string {
  if (typeof value === "number") {
    return format === "currency"
      ? new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value)
      : value.toLocaleString();
  }
  if (value === undefined || value === null) return "—";
  return String(value);
}
